/**
 * Filter Pill Primitives
 * ======================
 * Shared building blocks for all filter pill components in the FilterPanel system.
 *
 * ARCHITECTURE OVERVIEW
 * ─────────────────────
 * The filter system has three layers:
 *
 * 1. PRIMITIVES (this file)
 *    - CSS class constants (PILL_BUTTON_BASE, PILL_BUTTON_IDLE, PILL_BUTTON_ACTIVE, etc.)
 *    - PillWrapper: the label + children layout used by every pill
 *    - CustomFilterPill: a generic exportable pill trigger shell for custom filter UIs
 *    - All shared TypeScript types (FilterField, FilterPanelProps, etc.)
 *
 * 2. PILL COMPONENTS (one file per field type)
 *    Each pill wraps <PillWrapper> + a <Popover> and handles its own open/close state.
 *    - filter-panel.tsx → FilterPill (private, for SelectFilterField — multi-select Command list)
 *    - date-range-pill.tsx → DateRangePill (exported, for DateRangeFilterField — calendar picker)
 *    - CustomFilterPill (this file, exported) — for any custom popover content
 *
 * 3. FILTER PANEL (filter-panel.tsx)
 *    - Orchestrates all pills, manages visibility, "Dodaj filtr" dropdown, clear-all
 *    - Accepts `extraHiddenItems` + `renderExtraPill` for custom pills added via the dropdown
 *
 * HOW TO ADD A NEW FILTER PILL TYPE
 * ──────────────────────────────────
 * Option A — New standard field type (data lives in FilterPanel's `value` Record):
 *   1. Add your type to the FilterField union here (e.g. `RangeFilterField`)
 *   2. Create `range-pill.tsx` following the DateRangePill pattern:
 *      - Export a `RangePill` component with `field`, `selected`, `onChange`, `onClear`, `labels` props
 *      - Use `PillWrapper` + a raw `<button>` with `PILL_BUTTON_BASE` + `PILL_BUTTON_IDLE/ACTIVE`
 *      - Wrap your custom popover content inside
 *   3. In `filter-panel.tsx`, add a branch inside `FilterPanel`'s render loop for your new type
 *   4. Export the new component from `filter-panel.tsx` re-exports section
 *
 *   `RangeFilterField` (below) and `range-pill.tsx` (`RangePill`) follow exactly this recipe —
 *   min/max numeric inputs, one-sided allowed, `selected = [minStr, maxStr]` (same 2-element
 *   convention as DateRangeFilterField). `TextFilterField` now renders too (internal `TextPill`
 *   in filter-panel.tsx) — `selected = [typedString]` or `[]` when cleared.
 *
 * Option B — Custom pill with external state (like NearPoiFilterSection):
 *   1. Use `CustomFilterPill` from this file as your pill trigger
 *   2. Wrap it with a `<Popover>` in your component, supply your own `<PopoverContent>`
 *   3. Register your pill in FilterPanel via `extraHiddenItems` + `renderExtraPill` props
 *      so it appears in the "Dodaj filtr" dropdown and is hidden until selected
 *   See: apps/web/src/features/pois/components/near-poi-filter-section.tsx for a working example
 */

import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { Locale } from 'date-fns';
import { cn } from '../lib/utils.js';

// ─── Field type definitions ───────────────────────────────────

export type ChipColor = 'red' | 'blue' | 'green' | 'amber' | 'purple' | 'teal';

export type SelectFilterField = {
  type: 'select';
  id: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  /** Kept for backward compat — unused in pill design. */
  chipColor?: ChipColor;
  /** If true, pill is always visible even when no values are selected. */
  pinned?: boolean;
};

export type TextFilterField = {
  type: 'text';
  id: string;
  label: string;
  placeholder?: string;
  pinned?: boolean;
};

export type DateRangeFilterField = {
  type: 'dateRange';
  id: string;
  label: string;
  pinned?: boolean;
  /** Optional locale for date formatting and calendar display. */
  locale?: Locale;
};

/**
 * Numeric Min/Max range pill — keyboard-first, no slider, no preset chips.
 * One-sided values are supported: `selected = [minStr, maxStr]`, either side
 * may be `''`. See RangePill (range-pill.tsx) for the popover UI and
 * `formatRangeValue` for the active-chip/pill text formatting (both-sided
 * "10–50 m²", min-only "1000+ PLN", max-only "≤ 5000").
 */
export type RangeFilterField = {
  type: 'range';
  id: string;
  label: string;
  pinned?: boolean;
  /** Unit suffix shown next to the Min/Max inputs and appended to formatted text (e.g. "m²", "PLN"). */
  unit?: string;
};

// ─── Grouped select (multi-section, cross-group search) ───────
//
// Generic multi-select popover split into labeled option groups (e.g. one
// group per admin-division level) with a SINGLE search box that filters
// across every group at once. Not location-specific — any consumer that
// needs "one flat selection, multiple labeled sections" can use this field
// type. See GroupedFilterPill (grouped-filter-pill.tsx) for the popover UI
// and lib/grouped-filter.ts for the bucket-by-group helper.

export type GroupedSelectOption = { value: string; label: string };

export type FilterOptionGroup = {
  /**
   * Stable, translation-independent identifier for this group (e.g. "region").
   * Never rendered — used by the consuming app to bucket selected values back
   * into domain-specific filter keys. MUST be unique within `groups`.
   */
  id: string;
  /** Translated display heading rendered above the group's options. */
  heading: string;
  options: GroupedSelectOption[];
};

export type GroupedSelectFilterField = {
  type: 'groupedSelect';
  id: string;
  label: string;
  groups: FilterOptionGroup[];
  pinned?: boolean;
  /** Placeholder for the single cross-group search box (translated by the caller). */
  searchPlaceholder?: string;
  /**
   * Optional info-hint content rendered next to the popover heading via `InfoHint`
   * (e.g. to explain non-default combination semantics). Translated by the caller.
   */
  infoHint?: React.ReactNode;
  /** Accessible label for the info-hint trigger. Required when `infoHint` is set. */
  infoHintLabel?: string;
  /** Popover width override (Tailwind class, e.g. `"w-72"`). Defaults to `"w-64"`. */
  popoverClassName?: string;
};

export type FilterField =
  | SelectFilterField
  | TextFilterField
  | DateRangeFilterField
  | GroupedSelectFilterField
  | RangeFilterField;

// ─── FilterPanel types ────────────────────────────────────────

export interface FilterPanelLabels {
  /** @deprecated declared but not rendered by any component; kept for API compatibility */
  allLabel: string;
  searchPlaceholder: string;
  getSelectedCountLabel: (count: number) => string;
  getClearAriaLabel: (fieldId: string) => string;
  clearAllLabel: string;
  addFilterLabel?: string;
  /** @deprecated unused in new pill design */
  filtersLabel?: string;
}

/**
 * An extra item that appears in the "Dodaj filtr" dropdown but is NOT
 * backed by FilterPanel's `value` Record. Used for custom pills (Option B above).
 */
export interface ExtraFilterItem {
  id: string;
  label: string;
}

/**
 * Translation-free labels for the per-field include/exclude toggle extension point.
 * Supplied by the consuming app; `packages/ui` stays translation-free.
 */
export interface ExcludeToggleLabels {
  includeLabel: string;
  excludeLabel: string;
  getToggleAriaLabel: (fieldId: string) => string;
}

export interface FilterPanelProps {
  fields: FilterField[];
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
  /**
   * Called when "Wyczyść wszystkie" (Clear all) is clicked, in addition to
   * `onChange`. Lets a caller clear filter-adjacent state it owns (e.g. a
   * search box passed via `leadingContent`) without FilterPanel knowing
   * about that state directly.
   */
  onClearAll?: () => void;
  isLoading?: boolean;
  labels: FilterPanelLabels;
  /**
   * Extra items to appear in the "Dodaj filtr" dropdown.
   * Hidden by default; pill rendered once the user selects the item.
   * Pair with `renderExtraPill`.
   */
  extraHiddenItems?: ExtraFilterItem[];
  /**
   * Called by FilterPanel to render each visible extra pill.
   * `id`     — the ExtraFilterItem id that was selected
   * `onHide` — call this to remove the pill back into the "Dodaj filtr" menu
   */
  renderExtraPill?: (id: string, onHide: () => void) => React.ReactNode;
  /**
   * Extra item ids that should be visible (as pills) on initial render,
   * without the user having to select them from the "Dodaj filtr" menu.
   * Used when the filter state is restored from URL params.
   */
  defaultVisibleExtraIds?: string[];
  /**
   * Called when an extra item is selected from the "Dodaj filtr" menu, right as
   * its pill becomes visible. Use this to apply value-derived filters (e.g. a
   * boolean "no photo" flag) at add-time, so there is no "added but not applied"
   * intermediate state. Fires before the pill renders.
   */
  onShowExtra?: (id: string) => void;
  /** @deprecated Use extraHiddenItems + renderExtraPill instead. */
  extraPills?: React.ReactNode;
  // ─── Per-field include/exclude extension point ───
  /**
   * Ids of `select` fields that support an include/exclude (negation) mode toggle.
   * The toggle is rendered inside the field's popover. Field VALUES still use the
   * normal select control; only the mode is toggled via this extension point.
   */
  excludableFieldIds?: string[];
  /** Ids of fields currently in exclude mode (renders the "≠" chip form). */
  excludedFieldIds?: string[];
  /** Called with the field id when its include/exclude toggle is activated. */
  onToggleExclude?: (fieldId: string) => void;
  /** Translation-free labels for the exclude toggle (required when excludableFieldIds is set). */
  excludeLabels?: ExcludeToggleLabels;
  /** Translation-free Min/Max/Apply/Clear labels for range pills (required when a 'range' field is present). */
  rangeLabels?: RangePillLabels;
}

/** Translation-free labels for the RangePill's Min/Max popover. */
export interface RangePillLabels {
  minLabel: string;
  maxLabel: string;
  applyLabel: string;
  clearLabel: string;
}

// ─── Shared pill CSS constants ────────────────────────────────
//
// All pill trigger buttons MUST use these classes so every pill
// looks identical regardless of what popover content it opens.

export const PILL_BUTTON_BASE =
  'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

/** Idle (no value selected): dashed border, muted appearance. */
export const PILL_BUTTON_IDLE =
  'border-dashed border-foreground/30 bg-background hover:bg-accent/50';

/** Active (value selected): solid border, value chips visible. */
export const PILL_BUTTON_ACTIVE = 'border-foreground/40 bg-background hover:bg-accent/50';

/** A chip inside an active pill showing one selected value. */
export const PILL_VALUE_CLASS = 'inline-flex items-center bg-muted/60 rounded px-1.5 py-0.5';

/** Text inside an idle pill (label / placeholder). */
export const PILL_VALUE_IDLE_CLASS =
  'inline-flex items-center bg-transparent rounded px-1.5 py-0.5';

// ─── PillWrapper ──────────────────────────────────────────────
//
// Every pill MUST be wrapped in PillWrapper so the floating label
// above the pill (e.g. "FORMAT", "STATUS") is consistent.

export function PillWrapper({
  label,
  showLabel,
  children,
}: {
  label: string;
  showLabel: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span
        className={cn(
          'text-[10px] font-medium uppercase tracking-wider h-3',
          showLabel ? 'text-muted-foreground' : 'text-transparent',
        )}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

// ─── CustomFilterPill ─────────────────────────────────────────
//
// A standalone pill trigger for CUSTOM filter UIs (Option B above).
// Renders the pill button shell — idle/active state, value chips, X button —
// using the same CSS primitives as every other pill.
//
// Use this when your filter has external state (not a FilterPanel `value` key)
// and/or custom popover content. Wrap it with a <Popover> in your component:
//
//   <Popover open={open} onOpenChange={setOpen}>
//     <PillWrapper label={t('...')} showLabel={isActive}>
//       <PopoverTrigger asChild>
//         <CustomFilterPill
//           label={t('...')}
//           isActive={isActive}
//           onClear={handleClear}
//           clearAriaLabel={t('...')}
//           data-testid="my-filter-trigger"
//         >
//           {/* Active content: value chips shown inside the pill */}
//           <span className={PILL_VALUE_CLASS}>{selectedLabel}</span>
//         </CustomFilterPill>
//       </PopoverTrigger>
//     </PillWrapper>
//     <PopoverContent>…your custom picker…</PopoverContent>
//   </Popover>
//
// Note: PillWrapper is intentionally kept outside CustomFilterPill so you control
// the `showLabel` prop (whether the floating label above the pill is visible).

export interface CustomFilterPillProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'className'
> {
  /** Label shown in idle state and used as the pill's accessible name */
  label: string;
  /** Whether the filter currently has an active value */
  isActive: boolean;
  /** Called when the user clicks the X button to clear the filter */
  onClear: (e: React.MouseEvent) => void;
  /** Accessible label for the clear (X) button */
  clearAriaLabel: string;
  /** data-testid for the clear (X) span — separate from the root button's data-testid */
  clearDataTestId?: string;
  /**
   * Active state content: rendered inside the pill when isActive=true.
   * Typically one or more <span className={PILL_VALUE_CLASS}> chips.
   */
  children?: React.ReactNode;
}

// ─── PillTriggerButton ────────────────────────────────────────
//
// Shared trigger-button shell for Option A field types whose active content is
// ONE piece of content (a single value chip, formatted text, etc.) — the
// idle/active button state, X-clear icon (with matching aria/keyboard wiring),
// and idle-state chevron are otherwise byte-identical across TextPill
// (filter-panel.tsx) and RangePill (range-pill.tsx). Wrap it in
// `<PopoverTrigger asChild>` from the calling pill component.
//
// FilterPill (filter-panel.tsx, pre-existing SelectFilterField pill) keeps its
// own hand-rolled trigger — its active content is a different shape (an
// exclude-mode "≠" indicator plus a multi-chip list in a flex-wrap wrapper)
// and was not part of the duplication this component was extracted to fix.

export interface PillTriggerButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'className' | 'onClick' | 'children'
> {
  fieldId: string;
  isActive: boolean;
  isLoading?: boolean;
  /** Popover open state, reflected as `aria-expanded`. */
  open: boolean;
  /** Label shown in idle state. */
  idleLabel: string;
  /** Active-state content, typically `<span className={PILL_VALUE_CLASS}>{...}</span>`. */
  activeContent: React.ReactNode;
  onClear: (e: React.MouseEvent | React.KeyboardEvent) => void;
  clearAriaLabel: string;
}

// forwardRef + `...rest` spread are both required here: callers wrap this in
// `<PopoverTrigger asChild>`, which clones its child element with a ref (for
// positioning/focus/outside-click detection) AND injects its own onClick/
// onPointerDown/data-state props that must land on the real <button> DOM node
// for the popover to actually open — a plain function component silently
// drops the ref, and dropping `...rest` silently drops Radix's handlers.
export const PillTriggerButton = React.forwardRef<HTMLButtonElement, PillTriggerButtonProps>(
  function PillTriggerButton(
    {
      fieldId,
      isActive,
      isLoading,
      open,
      idleLabel,
      activeContent,
      onClear,
      clearAriaLabel,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        role="combobox"
        aria-expanded={open}
        data-testid={`filter-trigger-${fieldId}`}
        disabled={isLoading}
        className={cn(PILL_BUTTON_BASE, isActive ? PILL_BUTTON_ACTIVE : PILL_BUTTON_IDLE)}
        {...rest}
      >
        {isActive ? (
          <>
            {activeContent}
            <span
              role="button"
              tabIndex={0}
              aria-label={clearAriaLabel}
              onClick={onClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onClear(e);
              }}
              className="shrink-0 hover:opacity-70 rounded-full p-0.5"
            >
              <X className="h-3 w-3 text-foreground/60" />
            </span>
          </>
        ) : (
          <>
            <span className={cn(PILL_VALUE_IDLE_CLASS, 'text-muted-foreground')}>{idleLabel}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
          </>
        )}
      </button>
    );
  },
);

export const CustomFilterPill = React.forwardRef<HTMLButtonElement, CustomFilterPillProps>(
  function CustomFilterPill(
    { label, isActive, onClear, clearAriaLabel, clearDataTestId, children, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        role="combobox"
        className={cn(PILL_BUTTON_BASE, isActive ? PILL_BUTTON_ACTIVE : PILL_BUTTON_IDLE)}
        {...rest}
      >
        {isActive ? (
          <>
            <span className="flex items-center gap-1 text-[11px] text-foreground">{children}</span>
            <span
              role="button"
              tabIndex={0}
              aria-label={clearAriaLabel}
              data-testid={clearDataTestId}
              onClick={(e) => {
                e.stopPropagation();
                onClear(e);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onClear(e as unknown as React.MouseEvent);
                }
              }}
              className="shrink-0 hover:opacity-70 rounded-full p-0.5"
            >
              <X className="h-3 w-3 text-foreground/60" />
            </span>
          </>
        ) : (
          <>
            <span className={cn(PILL_VALUE_IDLE_CLASS, 'text-muted-foreground')}>{label}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
          </>
        )}
      </button>
    );
  },
);
